import React, { useState } from 'react';
import { FAILED, LOADING, READY } from 'lib/Status';
import { makeStyles } from '@material-ui/core';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import Checkbox from '@material-ui/core/Checkbox';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogContentText from '@material-ui/core/DialogContentText';
import DialogTitle from '@material-ui/core/DialogTitle';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import FormGroup from '@material-ui/core/FormGroup';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import Paper from '@material-ui/core/Paper';
import Skeleton from '@material-ui/lab/Skeleton';
import TextField from '@material-ui/core/TextField';
import Typography from '@material-ui/core/Typography';
import { teamRoles } from 'lib/constants';

const ROLES = Object.values(teamRoles);

// The search row is sized explicitly rather than by the flex row, so neither
// control can take its height from the other and grow unbounded.
const SEARCH_ROW_HEIGHT = 40;

const useStyles = makeStyles((theme) => ({
  // The app theme puts marginTop: 16px on every MuiTextField root, which is
  // right for stacked forms but pushes this one below the button beside it.
  searchField: {
    marginTop: 0,
  },
  searchButton: {
    flexShrink: 0,
    height: SEARCH_ROW_HEIGHT,
    marginLeft: theme.spacing(1),
  },
}));

export default function TeamAddUserDialog({
  isSubmitting,
  onClose,
  onConfirm,
  searchUsers,
  teamName,
  users,
}) {
  const styles = useStyles();
  const [term, setTerm] = useState('');
  const [submittedTerm, setSubmittedTerm] = useState(null);
  const [selectedUser, setSelectedUser] = useState(null);
  const [selectedRoles, setSelectedRoles] = useState([teamRoles.MEMBER]);

  // Only trust a response that answers the search currently in the box. Two
  // searches can be in flight at once and the slice is last-write-wins, so a
  // slow first response can land after a fast second one - showing one term's
  // people under another term's query, and adding the wrong user to the team.
  const answersCurrentSearch = Boolean(
    submittedTerm !== null
    && term.trim() === submittedTerm
    && users
    && users.meta.term === submittedTerm
  );

  const results = (answersCurrentSearch && users.meta.status === READY && users.payload) || [];
  const isSearching = Boolean(answersCurrentSearch && users.meta.status === LOADING);
  const hasFailed = Boolean(answersCurrentSearch && users.meta.status === FAILED);
  const isEmpty = Boolean(answersCurrentSearch && users.meta.status === READY && !results.length);

  const handleSearch = (event) => {
    event.preventDefault();
    const trimmed = term.trim();
    if (!trimmed) {
      return;
    }
    setSubmittedTerm(trimmed);
    searchUsers({ term: trimmed });
  };

  const handleToggleRole = (role) => {
    setSelectedRoles((current) => (
      current.includes(role)
        ? current.filter((r) => r !== role)
        : [...current, role]
    ));
  };

  const canConfirm = Boolean(selectedUser) && selectedRoles.length > 0 && !isSubmitting;

  return (
    <Dialog
      disableBackdropClick={isSubmitting}
      disableEscapeKeyDown={isSubmitting}
      fullWidth
      maxWidth="sm"
      onClose={onClose}
      open={true}
    >
      <DialogTitle>Add User To Team</DialogTitle>
      <DialogContent>
        <DialogContentText>
          Find an existing user and add them straight to
          {teamName ? ` ${teamName}` : ' this team'}. They are added
          immediately, without an invite.
        </DialogContentText>

        {selectedUser ? (
          <Paper variant="outlined">
            <Box p={2} display="flex" justifyContent="space-between" alignItems="center">
              <Box>
                <Typography variant="subtitle1">{selectedUser.name}</Typography>
                <Typography variant="body2" color="textSecondary">
                  {selectedUser.email}
                </Typography>
              </Box>
              <Button onClick={() => setSelectedUser(null)}>
                Change
              </Button>
            </Box>
          </Paper>
        ) : (
          <>
            <form onSubmit={handleSearch}>
              <Box display="flex" alignItems="center" mt={2} mb={1}>
                <TextField
                  autoFocus
                  className={styles.searchField}
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  label="Search by email or name"
                  margin="none"
                  onChange={(e) => setTerm(e.target.value)}
                  size="small"
                  value={term}
                  variant="outlined"
                />
                <Button
                  className={styles.searchButton}
                  color="primary"
                  disabled={!term.trim()}
                  type="submit"
                  variant="outlined"
                >
                  Search
                </Button>
              </Box>
            </form>

            {isSearching && (
              <Box py={1}>
                <Skeleton variant="text" />
                <Skeleton variant="text" />
              </Box>
            )}

            {hasFailed && (
              <Box py={2} textAlign="center" fontStyle="italic" color="error.main">
                Search failed. Try again.
              </Box>
            )}

            {isEmpty && (
              <Box py={2} textAlign="center" fontStyle="italic">
                No users match that search.
              </Box>
            )}

            {results.length > 0 && (
              <Paper variant="outlined">
                <List dense disablePadding>
                  {results.map((user) => (
                    <ListItem
                      button
                      key={user.id}
                      onClick={() => setSelectedUser(user)}
                    >
                      <ListItemText primary={user.name} secondary={user.email} />
                    </ListItem>
                  ))}
                </List>
              </Paper>
            )}
          </>
        )}

        <Box mt={2}>
          <Typography variant="subtitle2">Roles</Typography>
          <FormGroup row>
            {ROLES.map((role) => (
              <FormControlLabel
                control={(
                  <Checkbox
                    checked={selectedRoles.includes(role)}
                    color="primary"
                    onChange={() => handleToggleRole(role)}
                  />
                )}
                key={role}
                label={role}
              />
            ))}
          </FormGroup>
          {selectedRoles.length === 0 && (
            <Typography variant="caption" color="error">
              Pick at least one role.
            </Typography>
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button disabled={isSubmitting} onClick={onClose}>Cancel</Button>
        <Button
          color="primary"
          disabled={!canConfirm}
          onClick={() => onConfirm({ userId: selectedUser.id, roles: selectedRoles })}
          variant="contained"
        >
          {isSubmitting ? 'Adding…' : 'Add To Team'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
